import { useState } from "react";
import { ArrowRight, Settings } from "lucide-react";
import { useTargetConnection } from "@/hooks/useApi";
import { useTranslation } from "react-i18next";
import { ScardShell, Pill } from "@/components/projects/project-card-shell";
import { TargetConnectionSheet } from "@/components/projects/TargetConnectionSheet";

interface TargetConnectionCardProps {
  projectId: string;
  /** Usado pela Overview (#428): em vez de abrir o próprio Sheet aqui, o
   * card navega para /connections, a casa canônica das conexões do projeto.
   * Em /connections, onde este card já vive, o Sheet continua sendo a UI de
   * configuração de verdade — só a Overview muda. */
  linkToConnections?: boolean;
  /** Usado pela seção Target system de /connections: o card grande só pede ao dono que abra o
   * modal (que vive lá), em vez de montar um Sheet próprio. */
  onConfigure?: () => void;
}

/** Card-resumo da conexão do ambiente alvo (GROW) por projeto — S-043.
 *
 * Mostra a conexão Development e abre o modal único (TargetConnectionSheet) onde se
 * cadastra e edita as conexões do alvo. Usado na Overview, onde só navega para
 * /connections. Configurar destrava o Architect e o Code Companion. */
export function TargetConnectionCard({
  projectId,
  linkToConnections,
  onConfigure,
}: TargetConnectionCardProps) {
  const { t } = useTranslation();
  const { data } = useTargetConnection(projectId, "development");
  const [open, setOpen] = useState(false);

  const configured = !!(data && data.status && data.configured !== false);
  const hasCreds = !!data?.has_credentials;

  const statusLabel = configured
    ? data?.status === "connected"
      ? t("Connected")
      : data?.status === "failed"
        ? t("Error")
        : t("Configured")
    : t("Not configured");
  const statusTone = data?.status === "connected" ? "ok" : "gold";

  const shell = (
    <ScardShell
      to={linkToConnections ? `/projects/${projectId}/connections#target-system` : undefined}
      overline={t("Target System")}
      title={t("GROW Connection")}
      pill={<Pill tone={configured ? statusTone : "gold"}>{statusLabel}</Pill>}
      stat={
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="font-brand font-extrabold text-[56px] leading-none tracking-[-0.035em] text-[#08254c]">
            GROW
          </span>
          <span className="font-brand font-normal italic text-[17px] leading-[1.1] tracking-[-0.015em] text-[#08254c]">
            S/4HANA Cloud
          </span>
        </div>
      }
      meta={[
        { k: t("Release"), v: data?.release || "—", mono: true },
        { k: t("Client"), v: data?.client || "—", mono: true },
        {
          k: t("SWC"),
          v: data?.software_component || "—",
          mono: true,
          hint: t("Software Component — the ABAP component and version of the target system."),
        },
        { k: t("Credentials"), v: hasCreds ? t("Configured") : t("Missing") },
        { k: t("Status"), v: statusLabel },
      ]}
      cta={
        <>
          <span className="flex items-center gap-1.5">
            <Settings className="w-3.5 h-3.5" />
            {linkToConnections ? t("View connections") : t("Configure target")}
          </span>
          <ArrowRight className="w-3.5 h-3.5" />
        </>
      }
    />
  );

  if (linkToConnections) {
    return <div className="h-full">{shell}</div>;
  }

  if (onConfigure) {
    return (
      <div className="h-full" onClick={onConfigure}>
        {shell}
      </div>
    );
  }

  return (
    <>
      <div className="h-full" onClick={() => setOpen(true)}>
        {shell}
      </div>
      <TargetConnectionSheet projectId={projectId} open={open} onOpenChange={setOpen} />
    </>
  );
}
